import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Root, Trigger, Content, Item, CheckboxItem } from '@radix-ui/react-dropdown-menu';
import type { DismissableLayerProps } from '@radix-ui/react-dismissable-layer';
import type {
  MenuContentProps,
  MenuItemProps,
  MenuCheckboxItemProps,
} from '@radix-ui/react-dropdown-menu';
import { Colors } from 'styles';
import { transparentize } from 'color2k';

export type InteractOutsideEvent = Parameters<
  NonNullable<DismissableLayerProps['onInteractOutside']>
>[0];

export const DropdownMenuRoot = Root;

type TriggerPublicProps = React.PropsWithChildren<
  React.ComponentPropsWithoutRef<typeof Trigger> & {
    asChild?: boolean;
    disabled?: boolean;
  }
>;

// Re-type Radix Trigger to accept children + asChild in TS
const TriggerFixed = Trigger as unknown as React.ForwardRefExoticComponent<
  TriggerPublicProps & React.RefAttributes<React.ElementRef<typeof Trigger>>
>;

export const DropdownMenuTrigger = React.forwardRef<
  React.ElementRef<typeof Trigger>,
  TriggerPublicProps
>(({ children, ...props }, ref) => (
  <TriggerFixed ref={ref} {...props}>
    {children}
  </TriggerFixed>
));

DropdownMenuTrigger.displayName = 'DropdownMenuTrigger';
export const DropdownMenuContent = styled(Content as React.ComponentType<MenuContentProps>)`
  background-color: ${Colors.gray2};
  min-width: 120px;
  padding: 6px 0;
  z-index: 1;
`;

type StyledDropdownMenuItemProps = {
  $isSelected: boolean;
};

const StyledDropdownMenuItem = styled(Item as React.ComponentType<MenuItemProps>)`
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  outline: none;
  border: 4px solid transparent;
  transition: background-color 0.2s ease-in-out;

  &:focus {
    background-color: ${Colors.gray5};
  }

  ${(props: StyledDropdownMenuItemProps) =>
    props.$isSelected &&
    css`
      background-color: ${transparentize(Colors.blue2, 0.92)};
    `}
`;

type GlowProps = {
  $isSelected: boolean;
};

const Glow = styled.div`
  padding: 2px 8px;
  ${(props: GlowProps) =>
    props.$isSelected &&
    css`
      background-color: ${transparentize(Colors.blue2, 0.92)};
    `}
`;

export type DropdownMenuItemProps = MenuItemProps & {
  isSelected: boolean;
  // TODO: Consider Dropdown API allowing composition to remove the need for this prop
  noGlow?: boolean;
  children: React.ReactNode;
};

export const DropdownMenuItem = ({
  isSelected,
  noGlow = false,
  children,
  ...rest
}: DropdownMenuItemProps) => {
  return (
    <StyledDropdownMenuItem {...rest} $isSelected={isSelected}>
      {noGlow ? children : <Glow $isSelected={isSelected}>{children}</Glow>}
    </StyledDropdownMenuItem>
  );
};

export type DropdownCheckboxMenuItemProps = MenuCheckboxItemProps & {
  group?: string;
  label: string;
  value: string;
  onDismiss?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  color?: string;
};

export const DropdownCheckboxMenuItem = ({
  label,
  value,
  checked,
  disabled,
  group,
  ...rest
}: DropdownCheckboxMenuItemProps) => {
  return (
    <CheckboxItem {...rest} checked={checked} disabled={disabled} asChild>
      <CheckboxMenuItem
        {...rest}
        label={label}
        value={value}
        group={group}
        checked={checked}
        disabled={disabled}
      />
    </CheckboxItem>
  );
};

const CheckboxMenuItem = forwardRef<HTMLInputElement, DropdownCheckboxMenuItemProps>(
  (
    {
      label,
      value,
      group = 'checkbox',
      checked,
      disabled = false,
      onDismiss,
      onCheckedChange,
      ...rest
    }: DropdownCheckboxMenuItemProps,
    ref
  ) => {
    return (
      <FormControlLabel
        label={label}
        data-testid={`filter-${group}-${value}`}
        disabled={disabled}
        control={
          <Checkbox
            slotProps={{
              input: {
                ref,
                onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Escape') {
                    // Clicking Escape should dismiss the dropdown menu.
                    // However, if we are in a MUI `Dialog`, its escape handler
                    // will trigger before the escape handler for Radix's `Dropdown.Content`,
                    // since both attach listeners to `body`.
                    e.stopPropagation();
                    onDismiss?.();
                  } else {
                    // There are other event handlers provided by `CheckboxItem`, but
                    // we handle those manually below by passing `onChange` directly
                    // to the checkbox. `onKeyDown` is the only one needed, and it is
                    // to facilitate keyboard navigation between items.
                    rest.onKeyDown?.(e);
                  }
                },
              },
            }}
            name={value}
            checked={checked === true}
            disabled={disabled}
            onChange={() => onCheckedChange?.(!checked)}
            color="primary"
            sx={{ color: rest.color ?? Colors.blue3 }}
          />
        }
      />
    );
  }
);
CheckboxMenuItem.displayName = 'CheckboxMenuItem';
